/**
 * PrivacyConsentRowView: the client's privacy agreement at registration, shared by the Mini App's
 * registration page and the studio website's registration step, phone and computer
 * (shared-client-screens D3, rows R1, R2). Props only. The policy is a link to a new tab; the app
 * passes `onOpenPolicy` to open it outside Telegram instead (PrivacyConsentRow). The website's
 * piece contract hands in its part attributes through `parts`.
 *
 * Visual authority: docs/mockups/client-dashboard-redesign/registration-consent.html
 * (CONFIRMED 2026-10-15). Theme tokens only.
 */
import type { MouseEvent } from 'react';
import { LEGAL_PAGE_PATHS } from '@telify/shared/browser';
import styles from './PrivacyConsentRow.module.css';

export const PRIVACY_CONSENT_ERROR = 'Tick the box to continue';

/** Attributes a host adds to the row's parts (the website's piece contract). */
type PartAttrs = Readonly<Record<string, string>>;

export interface PrivacyConsentRowViewProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** Shown after an unticked submit; cleared by ticking. */
  showError: boolean;
  /** Opens the policy instead of the link's own new tab (the app: outside Telegram). */
  onOpenPolicy?: (url: string) => void;
  /** The error's element id, for the box's aria-describedby. */
  errorId?: string;
  parts?: { readonly row?: PartAttrs; readonly link?: PartAttrs; readonly error?: PartAttrs };
}

export function PrivacyConsentRowView({
  checked,
  onChange,
  showError,
  onOpenPolicy,
  errorId = 'privacy-consent-error',
  parts,
}: PrivacyConsentRowViewProps) {
  const openPolicy = (event: MouseEvent) => {
    // The link sits inside the label: open the page without toggling the box.
    event.stopPropagation();
    if (!onOpenPolicy) return;
    event.preventDefault();
    onOpenPolicy(new URL(LEGAL_PAGE_PATHS.privacyPolicy, window.location.origin).toString());
  };
  return (
    <div className={styles.wrap}>
      <label className={`${styles.row} ${showError ? styles.rowError : ''}`} {...parts?.row}>
        <input
          type="checkbox"
          className={styles.input}
          checked={checked}
          onChange={(event) => onChange(event.target.checked)}
          aria-invalid={showError || undefined}
          aria-describedby={showError ? errorId : undefined}
        />
        <span className={styles.box} aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </span>
        <span className={styles.label}>
          I agree to the{' '}
          <a href={LEGAL_PAGE_PATHS.privacyPolicy} target="_blank" rel="noopener noreferrer" onClick={openPolicy} {...parts?.link}>
            Privacy Policy
          </a>
        </span>
      </label>
      {showError && (
        <div id={errorId} className={styles.error} role="alert" {...parts?.error}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          {PRIVACY_CONSENT_ERROR}
        </div>
      )}
    </div>
  );
}
